import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, Home, Search, Plus, LayoutList, User, QrCode } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { AnkaraStrip } from "./ui";

export const LangToggle = ({ dark }) => {
  const { lang, setLang } = useApp();
  return (
    <div role="group" aria-label="Language" className={`flex rounded-full p-1 ${dark ? "bg-white/10" : "bg-[#F3EDE4]"}`}>
      {["en", "fr"].map((l) => (
        <button key={l} data-testid={`lang-toggle-${l}`} onClick={() => setLang(l)} aria-pressed={lang === l}
          className={`press min-w-[44px] min-h-[36px] rounded-full text-sm font-bold uppercase ${lang === l ? "bg-[#F59E0B] text-[#291E18]" : dark ? "text-white/80" : "text-[#6B5E55]"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

export const TopBar = () => {
  const { t, user, unread, setAuth } = useApp();
  return (
    <header className="sticky top-0 z-40 bg-[#1E4D3B] text-white">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <img src="/icon.svg" alt="" className="w-9 h-9 rounded-xl" />
          <span className={`font-extrabold text-lg sm:text-xl tracking-tight text-[#F59E0B] ${user ? "" : "max-[379px]:hidden"}`}>Diaspomark</span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link to="/share" data-testid="topbar-share" aria-label={t("share_app")} className={`press w-11 h-11 rounded-full items-center justify-center hover:bg-white/10 ${user ? "flex" : "hidden sm:flex"}`}><QrCode className="w-5 h-5" /></Link>
          <LangToggle dark />
          {!user && <button data-testid="topbar-join" onClick={() => setAuth({ mode: "signup" })} className="press min-h-[40px] px-4 rounded-full bg-[#F59E0B] text-[#291E18] text-sm font-extrabold whitespace-nowrap">{t("nav_join")}</button>}
          {user && (
            <Link to="/notifications" data-testid="topbar-notifications" aria-label={t("notifications")} className="press relative w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/10">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span data-testid="unread-badge" className="absolute top-1 right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#F59E0B] text-[#291E18] text-xs font-extrabold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
            </Link>
          )}
        </div>
      </div>
      <AnkaraStrip thin />
    </header>
  );
};

const Item = ({ to, icon: I, label, testid, badge }) => (
  <NavLink to={to} end={to === "/"} data-testid={testid}
    className={({ isActive }) => `press relative flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] font-semibold ${isActive ? "text-[#1E4D3B]" : "text-[#6B5E55]"}`}>
    {({ isActive }) => (<>
      <span className={`relative transition-transform duration-200 ${isActive ? "-translate-y-0.5" : ""}`}>
        <I className="w-6 h-6" strokeWidth={isActive ? 2.5 : 2} />
        {badge > 0 && <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C2410C] text-white text-[10px] font-bold flex items-center justify-center">{badge > 9 ? "9+" : badge}</span>}
      </span>
      <span className="whitespace-nowrap">{label}</span>
      {isActive && <span className="absolute bottom-1 w-6 h-1 rounded-full bg-[#F59E0B]" />}
    </>)}
  </NavLink>
);

export const BottomNav = () => {
  const { t, unread, user, setAuth } = useApp();
  const navigate = useNavigate();
  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#FFFDF9]/95 backdrop-blur-xl border-t border-[#E8E2D9] pb-[env(safe-area-inset-bottom)]" aria-label="Main">
      <div className="max-w-xl mx-auto flex items-end px-2">
        <Item to="/" icon={Home} label={t("home")} testid="nav-home" />
        <Item to="/explore" icon={Search} label={t("explore")} testid="nav-explore" />
        <div className="flex-1 flex justify-center">
          <button data-testid="nav-create" aria-label={user ? t("nav_post") : t("nav_join")} onClick={() => (user ? navigate("/create") : setAuth({ mode: "signup" }))}
            className="press -mt-7 flex flex-col items-center gap-0.5">
            <span className="w-16 h-16 rounded-full bg-[#F59E0B] hover:bg-[#FBBF24] text-[#291E18] flex items-center justify-center shadow-[0_8px_24px_rgba(217,119,6,0.45)] ring-4 ring-[#FAF7F2]"><Plus className="w-8 h-8" strokeWidth={3} /></span>
            <span data-testid="nav-create-label" className="text-[11px] font-extrabold text-[#92400E] whitespace-nowrap">{user ? t("nav_post") : t("nav_join")}</span>
          </button>
        </div>
        <Item to="/my-posts" icon={LayoutList} label={t("nav_my_posts")} testid="nav-my-posts" />
        <Item to="/profile" icon={User} label={t("profile")} testid="nav-profile" badge={unread} />
      </div>
    </nav>
  );
};

export const Footer = () => {
  const { t } = useApp();
  return (
    <footer className="mt-12 bg-[#1E4D3B] text-white/90">
      <AnkaraStrip thin />
      <div className="max-w-6xl mx-auto px-5 py-8 space-y-2">
        <div className="font-extrabold text-[#F59E0B] text-lg">Diaspomark</div>
        <p data-testid="footer-safety" className="text-sm leading-relaxed max-w-xl">{t("safety_footer")}</p>
        <p className="text-xs text-white/60">{t("marketing")}</p>
        <Link to="/share" data-testid="footer-share-link" className="press inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-[#F59E0B] text-[#291E18] font-bold text-sm"><QrCode className="w-4 h-4" />{t("share_app")}</Link>
      </div>
    </footer>
  );
};
